import { type Tuple } from "@mantine/core";
import { type CustomThemeData } from "../interfaces/customTheme";

export interface ThemeColors {
  main: Tuple<string, 10>;
  selectedHeaderCell: Tuple<string, 1>;
  headerColor: Tuple<string, 10>;
  headerSky: string[];
  headerSea: string[];
  background: string[];
  cardBackground: string[];
  cardBoxShadow: string[];
  thColor: string[];
  thBackgroundColor: string[];
  thBorder: string[];
  trHoverBackgroundColor: string[];
  tdBackgroundColor: string[];
  settingsSectionBg: string[];
  settingsColor: string[];
  logbookSectionBg: string[];
  redText: string[];
  grayText: string[];
  selectItemHoverBg: string[];
  summaryStatusGold: string[];
  inputBg: string[];
  inputBackgroud: string[];
  inputBorder: string[];
  text: string[];
  grayscale: Tuple<string, 4>;
  grayscaleText: Tuple<string, 1>;
  grayscaleBorder: Tuple<string, 1>;
}

export interface ThemeReturn {
  colorScheme?: string;
  primaryColor?: string;
  hideHeader?: boolean;
  colors: ThemeColors;
}

const makeColorArray = (color: string) =>
  new Array(10).fill(color) as Tuple<string, 10>;

const commonColors = (isDarkMode: boolean) => ({
  grayscale: (isDarkMode
    ? ["#666", "#4a4a4a", "#2d2d2d", "#3c3c3c"]
    : ["#999", "#bbb", "#eee", "#f6f6f6"]) as Tuple<string, 4>,
  grayscaleText: (isDarkMode ? ["#aaa"] : ["#555"]) as Tuple<string, 1>,
  grayscaleBorder: (isDarkMode ? ["#555"] : ["#6f6f6f"]) as Tuple<string, 1>,
  panelText: isDarkMode ? ["#989fa5"] : ["#1e3a5f"],
  panelTextActive: isDarkMode ? ["#ebf2ff"] : ["#0b1525"],
  panelHover: isDarkMode ? ["#4e6a8f"] : ["#d8edff"],
});

export const generateDefaultTheme: (
  isDarkMode: boolean,
) => ThemeReturn = isDarkMode => ({
  colorScheme: isDarkMode ? "dark" : "light",
  primaryColor: "main",
  colors: {
    main: [
      "#E7F5FF",
      "#D0EBFF",
      "#A5D8FF",
      "#74C0FC",
      "#4DABF7",
      "#339AF0",
      "#228BE6",
      "#1C7ED6",
      "#1971C2",
      "#1864AB",
    ],
    selectedHeaderCell: ["#09355f"],
    headerColor: makeColorArray("#E7F5FF"),
    headerSky: isDarkMode
      ? [
          "linear-gradient(to right,#05131f 15%,rgba(8, 62, 112, 1) 50%,#05131f 85%)",
        ]
      : [
          "radial-gradient(circle,rgba(24, 100, 171, 1) 0%,rgba(2, 6, 10, 1) 100%)",
        ],
    headerSea: ["#0f4c7e"],
    background: isDarkMode ? ["#081c2e"] : ["#f8f9fa"],
    cardBackground: isDarkMode ? ["#081c2e"] : ["#f8f9fa"],
    cardBoxShadow: isDarkMode ? ["#56a2e98f"] : ["#0000003d"],
    thColor: ["#F0F8FF"],
    thBackgroundColor: isDarkMode ? ["#0f4c7e"] : ["#1971c2"],
    thBorder: isDarkMode ? ["#1864ab"] : ["#93b7d9"],
    trHoverBackgroundColor: isDarkMode ? ["#16293a"] : ["#d8edff"],
    tdBackgroundColor: isDarkMode
      ? ["#1f354a", "#27425c"]
      : ["#f0f8ff", "#e3f1ff"],
    settingsSectionBg: isDarkMode ? ["#09131C"] : ["white"],
    settingsColor: isDarkMode ? ["#4dabf7"] : ["#1864ab"],
    logbookSectionBg: isDarkMode ? ["#09131C"] : ["#f3f5f7"],
    redText: ["#c92a2a"],
    grayText: ["#495057"],
    selectItemHoverBg: isDarkMode ? ["#343A40"] : ["#F1F3F5"],
    summaryStatusGold: isDarkMode ? ["#ffd700"] : ["#ccb11b"],
    inputBg: isDarkMode ? ["#081c2e"] : ["#E9ECEF"],
    inputBackgroud: isDarkMode ? ["#25262c"] : ["#ffffff"],
    inputBorder: isDarkMode ? ["#2b323b"] : ["#ced4da"],
    text: isDarkMode ? ["#ffffff"] : ["#000000"],
    ...commonColors(isDarkMode),
  },
});

export const generateDSNMTheme: (
  isDarkMode: boolean,
) => ThemeReturn = isDarkMode => ({
  colorScheme: isDarkMode ? "dark" : "light",
  primaryColor: "main",
  colors: {
    main: [
      "#f8f6ed",
      "#edeae1",
      "#d9d3c3",
      "#c5bba2",
      "#b3a685",
      "#a89973",
      "#a39268",
      "#8e7e57",
      "#7f704b",
      "#6e613b",
    ],
    selectedHeaderCell: ["#0D0E11"],
    headerColor: makeColorArray("#a89973"),
    headerSky: ["#0D0E11"],
    headerSea: ["#0D0E11"],
    background: isDarkMode ? ["#13110B"] : ["#f8f9fa"],
    cardBackground: isDarkMode ? ["#0D0E11"] : ["#f8f9fa"],
    cardBoxShadow: isDarkMode ? ["#6e613b"] : ["#0000003d"],
    thColor: isDarkMode ? ["#a89973"] : ["#fff"],
    thBackgroundColor: isDarkMode ? ["#1e1c14"] : ["#7f704b"],
    thBorder: isDarkMode ? ["#a89973"] : ["#f8f6ed"],
    trHoverBackgroundColor: isDarkMode ? ["#1e1c14"] : ["#d9d3c3"],
    tdBackgroundColor: isDarkMode
      ? ["#0D0E11", "#16181c"]
      : ["#f8f6ed", "#f7f2d7"],
    settingsSectionBg: isDarkMode ? ["#0D0E11"] : ["white"],
    settingsColor: ["#6e613b"],
    logbookSectionBg: isDarkMode ? ["#080807"] : ["#f3f5f7"],
    redText: ["#c92a2a"],
    grayText: ["#495057"],
    selectItemHoverBg: isDarkMode ? ["#343A40"] : ["#F1F3F5"],
    summaryStatusGold: isDarkMode ? ["#ffd700"] : ["#ccb11b"],
    inputBg: isDarkMode ? ["#081c2e"] : ["#E9ECEF"],
    inputBackgroud: isDarkMode ? ["#25262c"] : ["#ffffff"],
    inputBorder: isDarkMode ? ["#2b323b"] : ["#ced4da"],
    text: isDarkMode ? ["#ffffff"] : ["#000000"],
    ...commonColors(isDarkMode),
  },
});

export const generateCustomTheme = (
  isDarkMode: boolean,
  customTheme: CustomThemeData,
): ThemeReturn => {
  const mode = isDarkMode ? "dark" : "light";
  const theme = customTheme[mode];

  if (!theme) {
    throw new Error(`Custom theme data not found for mode "${mode}"`);
  }

  return {
    colorScheme: mode,
    primaryColor: "main",
    hideHeader: customTheme.hideHeader,
    colors: {
      main: makeColorArray(theme.buttonColor),
      selectedHeaderCell: [theme.selectedHeaderCellColor],
      headerColor: makeColorArray(theme.headerTextColor),
      headerSky: [
        !customTheme.hideHeader && theme.headerSkyGradient
          ? theme.headerSkyGradient
          : "none",
      ],
      headerSea: [
        !customTheme.hideHeader && theme.headerSeaColor
          ? theme.headerSeaColor
          : "none",
      ],
      background: [theme.backgroundColor],
      cardBackground: [theme.cardBackgroundColor],
      cardBoxShadow: [theme.cardBoxShadowColor],
      thColor: [theme.tableHeaderTextColor],
      thBackgroundColor: [theme.tableHeaderBackground],
      thBorder: [theme.tableHeaderBorderColor],
      trHoverBackgroundColor: [theme.tableRowHoverBackground],
      tdBackgroundColor: Array.isArray(theme.tableCellBackgrounds)
        ? theme.tableCellBackgrounds
        : [theme.tableCellBackgrounds, theme.tableCellBackgrounds],
      settingsSectionBg: [theme.settingsSectionBackground],
      settingsColor: [theme.settingsTextColor],
      logbookSectionBg: [theme.logbookSectionBackground],
      redText: [theme.redTextColor],
      grayText: [theme.grayTextColor],
      selectItemHoverBg: [theme.selectItemHoverBackground],
      summaryStatusGold: [theme.summaryStatusGoldColor],
      inputBg: [theme.inputBackgroundColor],
      inputBackgroud: [theme.inputBackgroundColorAlt],
      inputBorder: [theme.inputBorderColor],
      text: [theme.textColor],
      ...commonColors(isDarkMode),
    },
  };
};
